import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import * as MapboxDraw from "@mapbox/mapbox-gl-draw";
import * as _ from 'underscore';
import { Feature, FeatureCollection } from 'geojson';

import { BlockDTO, BlockGPSPointDTO } from '../../../../../harvest-engine/core/models/block'
import { FieldEventDTO } from '../../../../../harvest-engine/modules/field-events/models/field-event';
import { FarmFeature } from '../../../../../harvest-engine/modules/farm-features/models/farm-feature-dto';
import { FilterResults } from '../../../../../harvest-engine/core/widgets/kw-filter-dropdown/models/filter-results';
import { FarmFeatureMarkerService } from '../../../../../harvest-engine/modules/overview-map/services/farm-feature-marker.service';
import { FieldEventMarkerService } from '../../../../../harvest-engine/modules/overview-map/services/field-event-marker.service';


@Component({
  selector: 'kw-block-map-display',
  templateUrl: './block-map-display.component.html',
  styleUrls: ['./block-map-display.component.scss']
})
export class BlockMapDisplayComponent implements OnInit, OnDestroy {


  @Input() blockId: number;
  @Input() blocks: BlockDTO[];
  @Input() editable: boolean;
  @Input() height: string = '30vh';
  @Input() outlineBorders: boolean = false;
  @Input() fieldEvents: FieldEventDTO[] = [];
  @Input() farmFeature: FarmFeature[] = [];
  @Input() PinSize: number = 20;
  @Input() isAssetMap: boolean = false;
  // Optional farm location, shown as the same red pin used on the farm edit map.
  @Input() farmPin: { name?: string; latitude?: number; longitude?: number; customerName?: string } = null;
  // When true, skip the plain default pins for field events so the interactive
  // (hover/click popup) markers from FieldEventMarkerService aren't covered.
  @Input() suppressDuplicateEventPins: boolean = false;

  @Output() loaded = new EventEmitter();

  private filterResults: FilterResults;


  lottieConfig = {
    path: 'https://res.cloudinary.com/kragworks/raw/upload/v1631255564/HarvestEngine/lottie/area-map.json',
    renderer: 'canvas',
    autoplay: true,
    loop: false
  };
  public noMap: boolean = false;


  public isLoading: boolean = false;
  public mapLoaded: boolean = false;
  gpsPointCount: number = 0;


  public currentMode: string = 'draw_polygon';
  map: mapboxgl.Map;
  draw: MapboxDraw;

  private beforePrintHandler = () => this.handleBeforePrint();
  private afterPrintHandler = () => this.handleAfterPrint();
  private lastBounds: mapboxgl.LngLatBounds | null = null;

  public printSnapshotSrc: string | null = null;

  constructor(
    private farmFeatureMarkerService: FarmFeatureMarkerService,
    private fieldEventMarkerService: FieldEventMarkerService,
    private changeDetectorRef: ChangeDetectorRef,

  ) { }

  ngOnInit() {
    window.addEventListener('beforeprint', this.beforePrintHandler);
    window.addEventListener('afterprint', this.afterPrintHandler);
  }

  ngOnDestroy() {
    window.removeEventListener('beforeprint', this.beforePrintHandler);
    window.removeEventListener('afterprint', this.afterPrintHandler);
  }

  // The print page's container has a different width/aspect ratio than the live view, and
  // browsers don't reflow to print-page dimensions until after 'beforeprint' fires. Re-running
  // fitBounds here would compute a camera against the wrong container size. Instead, capture
  // whatever is already correctly rendered on screen and print that static image untouched.
  // Captured proactively once the map goes idle (see setMap), and refreshed here in case the
  // live map was panned after that. If capture ever fails, printSnapshotSrc stays unset and
  // the live canvas (never force-hidden unless a snapshot exists) prints instead of nothing.
  private captureSnapshot() {
    if (!this.map) return;
    try {
      this.map.once('idle', () => {
  this.printSnapshotSrc = this.map.getCanvas().toDataURL('image/png');
});
    } catch (e) {
      console.warn('[BlockMapDisplay] Could not capture map canvas for print:', e);
    }
  }

  private handleBeforePrint() {
    this.captureSnapshot();
    this.changeDetectorRef.detectChanges();
  }

  private handleAfterPrint() {
    this.printSnapshotSrc = null;
  }

  // Called by report components that control their own print sequence and can await this
  // before calling window.print(). Temporarily enlarges the live map, re-fits it against the
  // known larger size, and captures a bigger/sharper snapshot than the on-screen render — then
  // restores the live view. Safe to await: only resolves once the capture is actually done.
  public captureLargePrintSnapshot(): Promise<void> {
    if (!this.map) return Promise.resolve();
    const container = this.map.getContainer();
    const originalWidth = container.style.width;
    const originalHeight = container.style.height;

    container.style.width = '1500px';
    container.style.height = '700px';
    this.map.resize();
    if (this.lastBounds && !this.lastBounds.isEmpty()) {
      this.map.fitBounds(this.lastBounds, { padding: 50, duration: 0, maxZoom: 18 });
    } else {
      this.map.jumpTo({ center: [-98.5795, 39.8283], zoom: 4 });
    }

    return new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        this.map.triggerRepaint();
        this.captureSnapshot();
        container.style.width = originalWidth;
        container.style.height = originalHeight;
        this.map.resize();
        resolve();
      };
      this.map.once('idle', finish);
      setTimeout(finish, 3000); // safety net if 'idle' never fires
    });
  }

  async setMap(event) {
    this.mapLoaded = true;
    // ngx-mapbox-gl v10 emits a MapboxEvent { target: Map } on (mapLoad), not the Map directly
    const map: mapboxgl.Map = event?.target ?? event;

    if (!map || typeof map.on !== 'function') {
      console.error('[BlockMapDisplay] Invalid map instance received from (mapLoad):', event);
      return;
    }

    this.map = map;

    if (this.editable) {
      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        styles: [
          {
            id: 'gl-draw-polygon-fill-inactive',
            type: 'fill',
            paint: {
              'fill-color': '#022bc9', // Dark color (black)
              'fill-opacity': 0.4 // Adjust opacity for visibility
            }
          },
          {
            id: 'gl-draw-polygon-stroke-inactive',
            type: 'line',
            paint: {
              'line-color': '#000000', // Same dark color for stroke
              'line-width': 1
            }
          },
          {
            id: 'gl-draw-polygon-and-line-vertex-halo-inactive',
            type: 'circle',
            paint: {
              'circle-radius': 4,
              'circle-color': '#FFF' // White for vertex halo
            }
          },
          {
            id: 'gl-draw-polygon-and-line-vertex-inactive',
            type: 'circle',
            paint: {
              'circle-radius': 3,
              'circle-color': '#000000' // Dark color for vertex
            }
          }
        ]
      });

      this.map.on('draw.modechange', (e) => {
        const data = this.draw.getAll();
        if (this.draw.getMode() == 'draw_point') {
          var pids = []
          const lid = data.features[data.features.length - 1].id
          data.features.forEach((f) => {
            if (f.geometry.type === 'Point' && f.id !== lid) {
              pids.push(f.id)
            }
          })
          this.draw.delete(pids);
        }

        this.currentMode = this.draw.getMode();
      });

      this.map.addControl(this.draw, 'top-right');
    }

    let bounds = new mapboxgl.LngLatBounds();

    if (this.farmPin && typeof this.farmPin.latitude === 'number' && typeof this.farmPin.longitude === 'number') {
      const popupEl = document.createElement('div');
      const nameEl = document.createElement('strong');
      nameEl.textContent = this.farmPin.name || '';
      popupEl.appendChild(nameEl);
      if (this.farmPin.customerName) {
        const customerEl = document.createElement('div');
        customerEl.textContent = this.farmPin.customerName;
        popupEl.appendChild(customerEl);
      }
      const farmMarker = new mapboxgl.Marker({ color: '#d32f2f' })
        .setLngLat([this.farmPin.longitude, this.farmPin.latitude])
        .setPopup(new mapboxgl.Popup({ offset: 25, closeButton: false }).setDOMContent(popupEl))
        .addTo(this.map);
      farmMarker.getElement().title = this.farmPin.name || '';
      bounds.extend([this.farmPin.longitude, this.farmPin.latitude]);
    }

    if (this.blocks || this.isAssetMap == true) {
      this.blocks.forEach((block, blockIndex) => {
        const blockKey = block.id != null ? block.id : `idx${blockIndex}`;
        if (block.gpsPoints) {
          let pointsGroup: Array<Array<BlockGPSPointDTO>> = [];
          pointsGroup = block.gpsPoints.reduce(function (r, a) {
            r[a.polygonId] = r[a.polygonId] || [];
            r[a.polygonId].push(a);
            return r;
          }, Object.create(null));

          Object.keys(pointsGroup).forEach(key => {
            let gpsPoints = _.sortBy(pointsGroup[key].filter(x => x.isActive), x => x.id);
            if (gpsPoints.length > 2) {
              let coordinates = [];
              coordinates.push(gpsPoints.some(x => x.sortOrder != null) ?
                gpsPoints.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)).map(x => [+x.longitude, +x.latitude]) :
                gpsPoints.map(x => [+x.longitude, +x.latitude]));

              /** Create additional point if starting and finishing coordinates are not currently overlapping  */
              if (+gpsPoints[0] != +gpsPoints[gpsPoints.length - 1]) {
                coordinates[0].push([+gpsPoints[0].longitude, +gpsPoints[0].latitude])
              }

              let feature = <Feature>{
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: coordinates
                },
              };

              // Only add to draw if editable, otherwise add directly to map
              if (this.editable) {
                this.draw.add(feature);
              } else {
                if (!this.map.getSource(`polygon-${blockKey}-${key}`)) {
                  this.map.addSource(`polygon-${blockKey}-${key}`, {
                    'type': 'geojson',
                    'data': feature
                  });
                }
                if (!this.map.getLayer(`polygon-${blockKey}-${key}`)) {
                  this.map.addLayer({
                    'id': `polygon-${blockKey}-${key}`,
                    'type': 'fill',
                    'source': `polygon-${blockKey}-${key}`,
                    'paint': {
                      'fill-color': '#022bc9',
                      'fill-opacity': 0.4
                    }
                  });
                }
                if (!this.map.getLayer(`polygon-outline-${blockKey}-${key}`)) {
                  this.map.addLayer({
                    'id': `polygon-outline-${blockKey}-${key}`,
                    'type': 'line',
                    'source': `polygon-${blockKey}-${key}`,
                    'paint': {
                      'line-color': '#000000',
                      'line-width': 1
                    }
                  });
                }
              }

              for (let point of gpsPoints) {
                bounds.extend([+point.longitude, +point.latitude]);
              }
            }
          });
        }

        if (block.latitude && block.longitude && +block.latitude != 0 && +block.longitude != 0 && this.isAssetMap == false) {
          let feature = <Feature>{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [+block.longitude, +block.latitude]
            }
          };

          if (this.editable) {
            this.draw.add(feature);
          } else {
            new mapboxgl.Marker()
              .setLngLat([+block.longitude, +block.latitude])
              .addTo(this.map);
          }
        }
      });

      this.farmFeature.forEach(feature => {
        if (feature.longitude && +feature.latitude && this.isAssetMap == true) {
          const marker = new mapboxgl.Marker({
            color: feature.farmFeatureTypeIconColor,
            draggable: false
          }).setLngLat([+feature.longitude, +feature.latitude])
            .addTo(this.map);
          bounds.extend([+feature.longitude, +feature.latitude]);
        }
      });

      if (this.isAssetMap) {
        const labelFeatures = this.farmFeature
          .filter(f => f.longitude && f.latitude)
          .map(f => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [+f.longitude, +f.latitude] },
            properties: { name: f.name || '' }
          }));

        if (labelFeatures.length > 0) {
          const addLabelLayer = () => {
            if (this.map.getSource('asset-name-labels')) { return; }
            this.map.addSource('asset-name-labels', {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: labelFeatures } as any
            });
            this.map.addLayer({
              id: 'asset-name-labels',
              type: 'symbol',
              source: 'asset-name-labels',
              layout: {
                'text-field': ['get', 'name'],
                'text-anchor': 'top',
                'text-offset': [0, 1.5],
                'text-size': 12,
                'text-allow-overlap': true
              },
              paint: {
                'text-color': '#000000',
                'text-halo-color': '#ffffff',
                'text-halo-width': 2
              }
            });
          };

          if (this.map.isStyleLoaded()) {
            addLabelLayer();
          } else {
            this.map.on('style.load', addLabelLayer);
          }
        }
      }

      this.farmFeatureMarkerService.initializeMarkers(this.map, this.farmFeature);

      this.fieldEventMarkerService.initializeMarkers(this.map, this.fieldEvents);

      this.fieldEvents.forEach(event => {
        if (+event.longitude && +event.latitude) {
          if (!this.suppressDuplicateEventPins) {
            new mapboxgl.Marker({
              color: event.fieldEventType.iconColor,
              draggable: false
            }).setLngLat([+event.longitude, +event.latitude])
              .addTo(this.map);
          }
          bounds.extend([+event.longitude, +event.latitude]);
        }
      });

      // if(this.isAssetMap == false){
      //   this.map.jumpTo({center:[+block.longitude, +block.latitude], zoom:17})
      // } else if (this.isAssetMap == true){
      //   this.map.jumpTo({center:[+feature.longitude, +feature.latitude], zoom:17})
      // }
      this.lastBounds = bounds;
      if (bounds.isEmpty()) {
        this.map.jumpTo({
          center: [-98.5795, 39.8283], // Center of the USA
          zoom: 4 // Adjust zoom level to fit the country
        });
      } else if (this.farmPin && bounds.getSouthWest().lng === bounds.getNorthEast().lng && bounds.getSouthWest().lat === bounds.getNorthEast().lat) {
        // Only the farm pin to show: use the same zoom as the farm edit map rather than maxZoom.
        this.map.jumpTo({ center: bounds.getCenter(), zoom: 13 });
      } else {
        this.map.fitBounds(bounds, { padding: 50, duration: 0, maxZoom: 18 });
      }


    }
    // }, 500)

    // Emit 'loaded' once the map has actually finished rendering (tiles/sources/layers
    // painted and the fitBounds/jumpTo camera transition settled), not after a guessed delay.
    // Falls back to a timeout in case 'idle' never fires (e.g. zero-size/hidden container).
    let loadedEmitted = false;
    const emitLoadedOnce = () => {
      if (loadedEmitted) return;
      loadedEmitted = true;
      this.captureSnapshot();
      this.loaded.emit();
    };
    this.map.once('idle', emitLoadedOnce);
    setTimeout(emitLoadedOnce, 5000);

  }
}
